import { TContent } from '@/types/Content.types';

// Deciding portrait vs. landscape for a video.
//
// The backend carries no orientation/aspect field yet, so this resolves it
// from whatever the client can actually observe. The three cases are NOT equally solid,
// and the `isConfident` flag says which is which so the layout can stay tolerant when we
// are guessing:
//
//   1. A backend orientation/aspectRatio field, once it exists — authoritative.
//   2. Mux: image.mux.com thumbnails are rendered at the asset's NATIVE aspect ratio, so
//      probing the poster gives the true shape before any player mounts. Reliable.
//   3. YouTube: the backend now persists a best-effort `isShort` flag (Short ⇒ portrait,
//      non-Short ⇒ landscape) — preferred when present. When it's null/undetermined we
//      fall back to a thumbnail probe, which is only weak evidence for YouTube (its
//      endpoints letterbox Shorts into 16:9), and otherwise default to landscape.

export type TVideoOrientation = 'portrait' | 'landscape';

export type TVideoAspect = {
  orientation: TVideoOrientation;
  // width / height. Drives the actual frame so the video is never letterboxed into a
  // box of the wrong shape.
  ratio: number;
  // False when we are defaulting rather than measuring.
  isConfident: boolean;
  // True until a probe settles — the caller renders the dimmed "delay" state rather than
  // committing to a layout it may have to change.
  isResolving: boolean;
};

export const LANDSCAPE_RATIO = 16 / 9;
export const PORTRAIT_RATIO = 9 / 16;

// A ratio this close to square is ambiguous; treat >= 1 as landscape.
export const orientationFor = (ratio: number): TVideoOrientation => (ratio >= 1 ? 'landscape' : 'portrait');

type TVideoDetails = {
  source?: string;
  mux?: { playbackId?: string; thumbnailUrl?: string; status?: string };
  // `isShort` is the backend's best-effort YouTube Short flag; null when undetermined.
  youtube?: { videoId?: string; thumbnailUrl?: string; isShort?: boolean | null };
  // A generic orientation/aspectRatio field is not in the DTO yet — read defensively so
  // this resolves the moment it ships (would supersede the per-source signals below).
  orientation?: unknown;
  aspectRatio?: unknown;
};

const readDetails = (content: TContent): TVideoDetails => (content.details ?? {}) as TVideoDetails;

// Accepts "16:9", "9/16" or a plain number, whichever shape the backend settles on.
const parseAspectRatio = (value: unknown): number | null => {
  if (typeof value === 'number' && value > 0) return value;
  if (typeof value !== 'string') return null;

  const [width, height] = value.split(/[:/]/).map(Number);

  return width > 0 && height > 0 ? width / height : null;
};

// Authoritative answer from the payload, if the backend has started sending one.
export const readDeclaredAspect = (content: TContent): TVideoAspect | null => {
  const details = readDetails(content);

  const ratio = parseAspectRatio(details.aspectRatio);
  if (ratio) return { orientation: orientationFor(ratio), ratio, isConfident: true, isResolving: false };

  if (details.orientation === 'portrait' || details.orientation === 'landscape') {
    return {
      orientation: details.orientation,
      ratio: details.orientation === 'portrait' ? PORTRAIT_RATIO : LANDSCAPE_RATIO,
      isConfident: true,
      isResolving: false,
    };
  }

  // YouTube Short flag (backend best-effort). A confirmed Short is portrait; a
  // confirmed non-Short is landscape. Preferred over the thumbnail probe, which is
  // unreliable for YouTube. `null`/undefined means undetermined — fall through to probe.
  if (details.source === 'youtube' && typeof details.youtube?.isShort === 'boolean') {
    const orientation: TVideoOrientation = details.youtube.isShort ? 'portrait' : 'landscape';

    return {
      orientation,
      ratio: orientation === 'portrait' ? PORTRAIT_RATIO : LANDSCAPE_RATIO,
      isConfident: true,
      isResolving: false,
    };
  }

  return null;
};

// The image whose dimensions stand in for the video's.
export const getProbeUrl = (content: TContent): string | null => {
  const details = readDetails(content);

  if (details.source === 'mux') {
    if (details.mux?.thumbnailUrl) return details.mux.thumbnailUrl;
    // Mux renders this at the asset's native aspect ratio.
    return details.mux?.playbackId ? `https://image.mux.com/${details.mux.playbackId}/thumbnail.jpg` : null;
  }

  if (details.source === 'youtube') {
    if (details.youtube?.thumbnailUrl) return details.youtube.thumbnailUrl;
    return details.youtube?.videoId ? `https://i.ytimg.com/vi/${details.youtube.videoId}/maxresdefault.jpg` : null;
  }

  return null;
};

// Probing the same URL repeatedly across items would refetch on every loop advance.
const probeCache = new Map<string, Promise<number | null>>();

// Reading naturalWidth/naturalHeight needs no CORS — only pixel access would.
export const probeImageRatio = (url: string): Promise<number | null> => {
  const cached = probeCache.get(url);
  if (cached) return cached;

  const probe = new Promise<number | null>((resolve) => {
    const image = new Image();
    image.onload = () => resolve(image.naturalWidth > 0 && image.naturalHeight > 0 ? image.naturalWidth / image.naturalHeight : null);
    image.onerror = () => resolve(null);
    image.src = url;
  });

  probeCache.set(url, probe);

  return probe;
};

export const fallbackAspectFor = (content: TContent): TVideoAspect => {
  const details = readDetails(content);

  // Mux uploads are creator-recorded and predominantly portrait; YouTube skews landscape
  // because Shorts are the minority of pasted links. Both are defaults, not measurements.
  const orientation: TVideoOrientation = details.source === 'mux' ? 'portrait' : 'landscape';

  return {
    orientation,
    ratio: orientation === 'portrait' ? PORTRAIT_RATIO : LANDSCAPE_RATIO,
    isConfident: false,
    isResolving: false,
  };
};

// Correction applied once a player reports its real dimensions. Mux exposes these on the
// media element; YouTube's iframe never will. Guarded structurally so an absent API is a
// no-op rather than a crash.
export const readElementRatio = (element: unknown): number | null => {
  const media = element as { videoWidth?: unknown; videoHeight?: unknown } | null;
  if (!media || typeof media.videoWidth !== 'number' || typeof media.videoHeight !== 'number') return null;

  return media.videoWidth > 0 && media.videoHeight > 0 ? media.videoWidth / media.videoHeight : null;
};
